import { Thermometer, Volume2, TrendingUp, Shield, CheckCircle2, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const BENEFITS = [{
  Icon: Thermometer,
  tag: "Climate control",
  metric: "−8°C",
  metricLabel: "garage temp on a 35°C day",
  title: "Sydney summers, finally tolerable.",
  desc: "The polyurethane core acts as a thermal blanket between you and the western sun. Western Sydney, Penrith, Liverpool, Blacktown — all see the biggest temperature drop.",
  bullets: ["Ideal for workshops &amp; gyms", "Protects rooms above the garage", "Lower aircon load on the house"]
}, {
  Icon: Volume2,
  tag: "Acoustic reduction",
  metric: "27 dB",
  metricLabel: "average noise drop",
  title: "The street outside, on mute.",
  desc: "Foam core absorbs and dampens. Living next to a main road, train line, surf break, or busy lane? You'll hear the difference the day it's installed — and so will the bedroom above the garage.",
  bullets: ["Quieter opener operation too", "Better for night-shift sleepers", "Critical near roads &amp; flight paths"]
}, {
  Icon: TrendingUp,
  tag: "Resale value",
  metric: "+$4–8k",
  metricLabel: "average property value lift",
  title: "Modern buyers expect it.",
  desc: "Real estate agents flag single-skin doors as a negotiation point against you. An insulated, ColorBond-matched door reads as a modern, well-kept home — and quietly adds thousands at sale.",
  bullets: ["Strong with buyers under 45", "Matches inspector checklists", "Stands out in listing photos"]
}, {
  Icon: Shield,
  tag: "Built tougher",
  metric: "15+ yrs",
  metricLabel: "typical service life",
  title: "Dent-resistant. Salt-resistant.",
  desc: "Two skins of pre-painted steel bonded to a polyurethane core gives panels that resist trolley dings, basketball strikes, and coastal corrosion the way single-skin doors simply can't.",
  bullets: ["Marine-grade options for beachside", "Colour holds through UV exposure", "Stiffer in high-wind events"]
}];
export default function InsulatedBenefits() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 20% 0%, ${NAVY_LIGHT}55 0%, ${NAVY} 50%), radial-gradient(circle at 85% 100%, ${GOLD}10 0%, transparent 40%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      left: "-30px",
      top: "40px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        03
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD
          }}>
              03 · Four Measurable Wins
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: "#ffffff",
          marginBottom: "20px"
        }}>
            Not just nicer.{" "}
            <span style={{
            color: GOLD
          }}>
              Measurably better.
            </span>
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.72)",
          maxWidth: "640px"
        }}>
            Insulated doors aren't a luxury — they're an upgrade with four
            specific, measurable returns. Here's how each one shows up.
          </p>
        </div>

        {/* 2x2 benefit grid */}
        <div className="grid md:grid-cols-2 gap-5">
          {BENEFITS.map(({
          Icon,
          tag,
          metric,
          metricLabel,
          title,
          desc,
          bullets
        }, i) => <article key={tag} className="relative rounded-2xl overflow-hidden transition-all hover:translate-y-[-2px] hover:shadow-2xl" style={{
          backgroundColor: `${NAVY_LIGHT}55`,
          border: `1px solid rgba(255,255,255,0.08)`,
          backdropFilter: "blur(6px)"
        }}>
                {/* Gold corner accent */}
                <div aria-hidden className="absolute top-0 left-0 h-px" style={{
            width: "80px",
            background: `linear-gradient(90deg, ${GOLD} 0%, transparent 100%)`
          }} />
                <div aria-hidden className="absolute top-0 left-0 w-px" style={{
            height: "80px",
            background: `linear-gradient(180deg, ${GOLD} 0%, transparent 100%)`
          }} />

                {/* Number stamp */}
                <div aria-hidden className="absolute bottom-4 right-6 pointer-events-none select-none" style={{
            fontFamily: FONT_HEAD,
            fontSize: "70px",
            fontWeight: 800,
            color: `${GOLD}08`,
            lineHeight: 1,
            letterSpacing: "-0.04em"
          }}>
                  {String(i + 1).padStart(2, "0")}
                </div>

                <div className="p-7 md:p-9 relative">
                  {/* Header row: icon + tag */}
                  <div className="flex items-center gap-3 mb-6">
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg" style={{
                width: 48,
                height: 48,
                backgroundColor: `${GOLD}15`,
                border: `1px solid ${GOLD}33`
              }}>
                      <Icon size={22} color={GOLD} strokeWidth={2.25} />
                    </span>
                    <span style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: GOLD
              }}>
                      {tag}
                    </span>
                  </div>

                  {/* Big metric */}
                  <div className="mb-5 pb-5 border-b" style={{
              borderColor: "rgba(255,255,255,0.1)"
            }}>
                    <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "clamp(3rem, 5vw, 4rem)",
                fontWeight: 900,
                lineHeight: 0.95,
                color: GOLD,
                letterSpacing: "-0.04em",
                marginBottom: "8px"
              }}>
                      {metric}
                    </div>
                    <div style={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                color: "rgba(255,255,255,0.6)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontWeight: 600
              }}>
                      {metricLabel}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1.4rem",
              lineHeight: 1.2,
              color: "#ffffff",
              marginBottom: "12px",
              letterSpacing: "-0.02em"
            }}>
                    {title}
                  </h3>

                  {/* Desc */}
                  <p style={{
              fontFamily: FONT_BODY,
              fontSize: "14.5px",
              lineHeight: 1.65,
              color: "rgba(255,255,255,0.72)",
              marginBottom: "18px"
            }}>
                    {desc}
                  </p>

                  {/* Bullets */}
                  <ul className="space-y-2">
                    {bullets.map(b => <li key={b} className="flex items-start gap-2" style={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                color: "rgba(255,255,255,0.78)",
                fontWeight: 500
              }}>
                        <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex flex-shrink-0 mt-0.5">
                          <CheckCircle2 size={13} color={GOLD} strokeWidth={2.5} />
                        </span>
                        <span dangerouslySetInnerHTML={{
                  __html: b
                }} />
                      </li>)}
                  </ul>
                </div>
              </article>)}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-12 rounded-2xl p-7 md:p-8 relative overflow-hidden" style={{
        background: `linear-gradient(135deg, ${NAVY_LIGHT}AA 0%, ${NAVY_DEEP} 100%)`,
        border: `1px solid ${GOLD}33`
      }}>
          {/* Decorative dots */}
          <div aria-hidden className="absolute top-0 right-0 w-40 h-40 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle, ${GOLD}33 1px, transparent 1.5px)`,
          backgroundSize: "12px 12px",
          opacity: 0.5,
          maskImage: "radial-gradient(circle at top right, black 0%, transparent 70%)"
        }} />

          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: "6px"
            }}>
                Free measure &amp; quote
              </div>
              <h3 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "clamp(1.4rem, 2.2vw, 1.85rem)",
              lineHeight: 1.2,
              color: "#ffffff",
              letterSpacing: "-0.015em"
            }}>
                Curious which benefit matters most for your home?
              </h3>
            </div>

            <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
              <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:scale-[1.02]" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "14px",
              boxShadow: `0 8px 24px ${GOLD}33`
            }}>
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Phone size={16} strokeWidth={2.5} />
                </span>
                Call 0485 000 933
              </a>

              <Link href="#contact-us" className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border transition-all hover:bg-white/10" style={{
              borderColor: "rgba(255,255,255,0.25)",
              color: "#ffffff",
              fontFamily: FONT_HEAD,
              fontWeight: 600,
              fontSize: "14px"
            }}>
                Free quote
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <ArrowRight size={14} strokeWidth={2.5} />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}